import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  adminOpsSettingsQueryOptions,
  llmUsageStatsQueryOptions,
  llmUsageTenantsQueryOptions,
  llmUsageTimeseriesQueryOptions,
  vertexUsageQueryOptions,
} from "@/lib/queries";
import { AdminBreakdownGrid, AdminFilterBar, AdminPage } from "@/features/admin/components/layout";
import { ADMIN_OPS_FALLBACK } from "@/features/admin/lib/admin-ops-settings";
import { EventsTimeRangeControl } from "@/features/events/components/EventsTimeRangeControl";
import { eventTimeRangeHours } from "@/features/events/constants/event-time-ranges";
import { useIsSuperTenant, usePermissions } from "@/features/auth/hooks/usePermissions";
import { LlmUsageChart } from "./LlmUsageChart";
import { LlmUsageFeatureTable } from "./LlmUsageFeatureTable";
import { LlmUsageKpiStrip } from "./LlmUsageKpiStrip";
import { LlmUsageModelTable } from "./LlmUsageModelTable";
import { LlmUsageScopeFilters } from "./LlmUsageScopeFilters";
import { LlmUsageTenantTable } from "./LlmUsageTenantTable";
import { alertThresholdForWindow } from "../lib/alert-highlight";
import {
  clearLlmUsageScopes,
  hasLlmUsageScope,
  type LlmUsageFiltersState,
} from "./llm-usage-filters";
import { VertexReconciliationDialog } from "./VertexReconciliationDialog";

interface LlmUsagePageProps {
  filters: LlmUsageFiltersState;
  onFiltersChange: (next: LlmUsageFiltersState) => void;
}

/**
 * Token spend per model over a rolling window, with the feature that spent it.
 *
 * Two sources sit on this page on purpose. The KPI strip, chart, and table are
 * what Nexus recorded, which is the only source that knows which tenant and
 * feature a call belongs to. The reconciliation beside the filters is what
 * Vertex billed, which is the only source that covers calls Nexus did not
 * make. Reading them together is what makes either trustworthy.
 */
export function LlmUsagePage({ filters, onFiltersChange }: LlmUsagePageProps) {
  const { t } = useTranslation();
  const isSuperTenant = useIsSuperTenant();
  const perms = usePermissions();
  const windowHours = eventTimeRangeHours(filters.range);

  const usageFilters = {
    windowHours,
    ...(filters.feature ? { feature: filters.feature } : {}),
    ...(filters.tenantId ? { tenantId: filters.tenantId } : {}),
    ...(filters.provider ? { provider: filters.provider } : {}),
    ...(filters.status === "failed" ? { status: "failed" as const } : {}),
  };

  // Whether this tab refreshes itself. A viewer without the grant gets the
  // same defaults a fresh install shows.
  const { data: opsSettings } = useQuery({
    ...adminOpsSettingsQueryOptions(),
    enabled: isSuperTenant || perms.ADMIN_GET_OPS_SETTINGS.isOn,
  });
  const ops = opsSettings ?? ADMIN_OPS_FALLBACK;
  const refetchInterval =
    ops.tokenUsageAutoRefreshSeconds > 0 ? ops.tokenUsageAutoRefreshSeconds * 1000 : false;

  const stats = useQuery({
    ...llmUsageStatsQueryOptions(usageFilters),
    refetchInterval,
    enabled: isSuperTenant,
  });
  const timeseries = useQuery({
    ...llmUsageTimeseriesQueryOptions(usageFilters),
    refetchInterval,
    enabled: isSuperTenant,
  });
  // Shared with the chart, which brands its tenant series from the same list.
  const tenants = useQuery({
    ...llmUsageTenantsQueryOptions(windowHours),
    enabled: isSuperTenant,
  });
  // Cloud Monitoring carries none of the dimensions this page filters on, so
  // the panel always reports the whole window — narrowing it by feature,
  // tenant, or provider would return the same numbers and imply otherwise.
  const vertex = useQuery({
    ...vertexUsageQueryOptions(windowHours),
    enabled: isSuperTenant,
  });

  // A number on screen is marked only when the alert behind it is live over
  // this exact window. Anything wider or narrower is a different measurement
  // than the sweep takes, and marking it would send an operator looking for a
  // Slack message that was never sent.
  const tenantSpendAlertTokens = alertThresholdForWindow({
    isEnabled: ops.alertOnTenantTokenSpend,
    threshold: ops.tenantTokenSpendAlertThreshold,
    alertWindowHours: ops.tokenSpendAlertWindowHours,
    windowHours,
  });
  const failureAlertPercent = alertThresholdForWindow({
    isEnabled: ops.alertOnLlmFailureRate,
    threshold: ops.llmFailureRateAlertPercent,
    alertWindowHours: ops.tokenSpendAlertWindowHours,
    windowHours,
  });
  const latencyAlertP95Ms = alertThresholdForWindow({
    isEnabled: ops.alertOnLlmLatency,
    threshold: ops.llmLatencyAlertP95Ms,
    alertWindowHours: ops.tokenSpendAlertWindowHours,
    windowHours,
  });

  return (
    <AdminPage
      filters={
        <AdminFilterBar
          range={
            <EventsTimeRangeControl
              value={filters.range}
              onChange={(range) => onFiltersChange({ ...filters, range })}
            />
          }
          isFiltered={hasLlmUsageScope(filters)}
          onClear={() => onFiltersChange(clearLlmUsageScopes(filters))}
          resultCount={t("admin.llmUsage.filters.modelCount", "{{count}} models", {
            count: stats.data?.byModel.length ?? 0,
          })}
          onRefresh={() => {
            void stats.refetch();
            void timeseries.refetch();
          }}
          isRefreshing={stats.isFetching || timeseries.isFetching}
          actions={
            // Sits with the filters rather than at the foot of the page: it is
            // one sentence about whether the numbers below add up, and it
            // should be readable before scrolling through them. The window is
            // the only filter it answers to — Cloud Monitoring carries no
            // tenant or feature dimension.
            <VertexReconciliationDialog
              data={vertex.data}
              isLoading={vertex.isLoading}
              recorded={{
                inputTokens: stats.data?.inputTokens ?? 0,
                outputTokens: stats.data?.outputTokens ?? 0,
                thinkingTokens: stats.data?.thinkingTokens ?? 0,
                cacheWriteTokens: stats.data?.cacheWriteTokens ?? 0,
                cacheReadTokens: stats.data?.cacheReadTokens ?? 0,
              }}
              longTailPercent={ops.tokenUsageLongTailPercent}
            />
          }
          scopes={
            <LlmUsageScopeFilters
              filters={filters}
              onFiltersChange={onFiltersChange}
              windowHours={windowHours}
              tenants={tenants.data}
              isTenantsLoading={tenants.isLoading}
            />
          }
        />
      }
      kpis={
        <LlmUsageKpiStrip
          stats={stats.data}
          isLoading={stats.isLoading}
          isStale={stats.isPlaceholderData}
          isError={stats.isError}
          onRetry={() => void stats.refetch()}
          latencyAlertP95Ms={latencyAlertP95Ms}
        />
      }
      chart={
        // Only the enabled alerts are drawn. A threshold that is configured but
        // switched off is a number nothing acts on, and a line for it would
        // claim a limit the platform does not enforce.
        <LlmUsageChart
          data={timeseries.data}
          isLoading={timeseries.isLoading}
          isPlaceholderData={timeseries.isPlaceholderData}
          windowHours={windowHours}
          tokenSpendAlert={
            ops.alertOnTokenSpend
              ? {
                  threshold: ops.tokenSpendAlertThreshold,
                  windowHours: ops.tokenSpendAlertWindowHours,
                }
              : null
          }
          latencyAlertP95Ms={ops.alertOnLlmLatency ? ops.llmLatencyAlertP95Ms : null}
          tenantOptions={tenants.data}
          defaultStackBy={ops.tokenUsageDefaultGrouping}
        />
      }
      breakdown={
        <>
          {/* The model table leads and takes the full width: it answers the
              question the tab exists for, and its seventh column would not fit
              beside a neighbour. */}
          <LlmUsageModelTable
            rows={stats.data?.byModel ?? []}
            isLoading={stats.isLoading}
            isStale={stats.isPlaceholderData}
            totalTokens={stats.data?.totalTokens ?? 0}
            longTailPercent={ops.tokenUsageLongTailPercent}
            failureAlertPercent={failureAlertPercent}
          />

          {/* One question from two sides — what the tokens went to, and who
              they went for — so the two sit together to be read against each
              other. They pair rather than the model table because both carry
              the same six measures, which keeps the columns aligned. */}
          <AdminBreakdownGrid>
            <LlmUsageFeatureTable
              rows={stats.data?.byFeature ?? []}
              isLoading={stats.isLoading}
              isStale={stats.isPlaceholderData}
              totalTokens={stats.data?.totalTokens ?? 0}
              longTailPercent={ops.tokenUsageLongTailPercent}
              failureAlertPercent={failureAlertPercent}
            />

            {/* Clicking the tenant already shown scopes the page back out, so the
              row is the way in and the way out — the dropdown is not the only
              exit. */}
            <LlmUsageTenantTable
              rows={stats.data?.byTenant ?? []}
              isLoading={stats.isLoading}
              isStale={stats.isPlaceholderData}
              totalTokens={stats.data?.totalTokens ?? 0}
              longTailPercent={ops.tokenUsageLongTailPercent}
              spendAlertTokens={tenantSpendAlertTokens}
              selectedTenantId={filters.tenantId}
              onSelectTenant={(tenantId) =>
                onFiltersChange({
                  ...filters,
                  tenantId: tenantId === filters.tenantId ? undefined : tenantId,
                })
              }
            />
          </AdminBreakdownGrid>
        </>
      }
    />
  );
}
